// src/components/docs/ApiDocsPro.jsx
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "react-query";
import { HiUser, HiOfficeBuilding } from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { ADMIN, API, CATEGORIES, QUERIES, USERS } from "../../config/apiConfig";
import { CopyBlock, dracula } from "react-code-blocks";
import { useApplication } from "../../contexts/ApplicationContext";
import DrawerLoading from "../DrawerLoading";
import { IoIosArrowDown } from "react-icons/io";
import ApiRequestExamples from "../docs/ApiRequestExamples";
import axiosInstance from "../../config/axiosInstance";
import { Spinner } from "flowbite-react";
import { FaTools } from "react-icons/fa";
import NegativeDocs from "../docs/NegativeDocs";
import { MdOutlineThumbDown } from "react-icons/md";

export default function ApiDocsPro() {
  const { user } = useApplication();
  const [selectedApi, setSelectedApi] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [testParam, setTestParam] = useState("");
  const [apiResponse, setApiResponse] = useState(null);
  const [isLoadingTest, setIsLoadingTest] = useState(false);
  const [testError, setTestError] = useState(null);

  const {
    data: categories,
    isLoading: loadingCategories,
    error: errorCategories,
  } = useQuery("categories", async () => {
    const { data } = await axios.get(`${CATEGORIES.GET_ALL}?itemsPerPage=-1`);
    return data.data.data;
  });

  const {
    isLoading: loadingQueries,
    error: errorQueries,
    data: queries,
  } = useQuery(["userQueryPermissions"], async () => {
    const { data: items } = await axios.get(
      `${USERS.GET_USER_QUERY_PERMISSIONS}`
    );
    return items.data;
  });

  const {
    isLoading: isLoadingToken,
    error: errorToken,
    data: token,
  } = useQuery(["token"], async () => {
    const data = await axios.get(`${API.GET_TOKEN}`);
    return data.data.token;
  });

  const { isLoading: isLoadingSelectedQuery, data: selectedQuery } = useQuery(
    ["queryCustomized", selectedApi?.id],
    async () => {
      // apenas busca se não for "custom"
      if (selectedApi?.isCustom) return null;
      const { data: items } = await axios.get(
        `${QUERIES.GET_QUERY}/${selectedApi?.id}`
      );
      return items.data;
    },
    { enabled: !!selectedApi?.id && !selectedApi?.isCustom }
  );

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // >>> NOVO: Categoria sintética para CV / Negativação
  const cvCategory = useMemo(() => {
    if (!user?.Company?.hasCV) return null;
    return {
      id: "__cv_docs__",
      name: "Negativação",
      isSynthetic: true,
      icon: <MdOutlineThumbDown className="w-4 h-4 text-green-600" />,
      apis: [
        {
          id: "__cv_docs_api__",
          name: "Negativação — API",
          description:
            "Rotas para criar/consultar cliente indireto, registrar negativação, consultar status e listar registros.",
          isCustom: true, // chave para render custom
          status: 1,
        },
      ],
    };
  }, [user?.Company?.hasCV]);

  const groupedApis = useMemo(() => {
    const base =
      categories?.map((category) => ({
        ...category,
        icon: category.name.toLowerCase().includes("pessoa física") ? (
          <HiUser className="w-4 h-4 text-green-600" />
        ) : (
          <HiOfficeBuilding className="w-4 h-4 text-green-600" />
        ),
        apis:
          queries
            ?.filter((q) => q.categoryId === category.id && q.status != -1)
            ?.filter((q) =>
              q.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            ?.sort((a, b) => a.name.localeCompare(b.name)) || [],
      })) || [];

    // injeta categoria CV no topo (se existir)
    if (cvCategory) {
      const nameMatch = "negativação";
      const alreadyHas =
        base.find(
          (c) => c.name?.toLowerCase() === nameMatch && c.id === cvCategory.id
        ) != null;
      if (!alreadyHas) {
        base.unshift(cvCategory);
      }
    }

    return base;
  }, [categories, queries, searchTerm, cvCategory]);

  useEffect(() => {
    if (groupedApis) {
      const defaultExpanded = {};
      groupedApis.forEach((category) => {
        defaultExpanded[category.id] = true;
      });
      setExpandedCategories(defaultExpanded);
    }
  }, [groupedApis]);

  useEffect(() => {
    setTestParam("");
    setApiResponse(null);
    setTestError(null);
  }, [selectedApi]);

  const getPathParam = (parameterType) => {
    switch (parameterType) {
      case "CPF":
        return ":cpf";
      case "CNPJ":
        return ":cnpj";
      case "CPF/CNPJ":
        return ":cpf-ou-cnpj";
      case "PLACA":
        return ":placa";
      case "CHASSI":
        return ":chassi";
      case "MOTOR":
        return ":motor";
      case "PLACA/CHASSI":
        return ":placa-ou-chassi";
      default:
        return ":param";
    }
  };

  const handleTestApi = async () => {
    setIsLoadingTest(true);
    setTestError(null);
    setApiResponse(null);

    try {
      const url = `/v2/${selectedApi?.url}/${testParam}`;
      const res = await axiosInstance.get(url);
      setApiResponse(res.data);
    } catch (err) {
      setTestError(err?.response?.data || "Erro ao fazer requisição.");
    } finally {
      setIsLoadingTest(false);
    }
  };

  const renderDefaultDocs = () => (
    <>
      <section className="mb-6">
        <div className="text-sm font-mono bg-gray-50 border border-gray-200 rounded-md p-4 text-gray-800">
          <div className="mb-2 text-gray-500 font-semibold">
            📥 URL de requisição:
          </div>
          <pre className="whitespace-pre-wrap overflow-hidden">
            <b>GET</b>{" "}
            {`${window.location.origin}/api-rscore/v2/${
              selectedApi?.url
            }/${getPathParam(selectedQuery?.QueryCustomized?.parameterType)}`}
          </pre>
        </div>
      </section>

      <section>
        <div>
          <div className="mb-3">
            <div className="font-semibold text-sm mb-1">
              🔗 Token de integração:
            </div>
            {token && <CopyBlock text={token} theme={dracula} codeBlock />}
          </div>
        </div>

        <section className=" mt-5">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            🔐 <span>Autenticação</span>
          </h3>

          <div className="border border-gray-200 bg-white p-4 rounded-xl shadow-sm text-sm text-gray-700 leading-relaxed">
            Esta API utiliza autenticação do tipo <strong>Bearer Token</strong>{" "}
            com
            <span className="font-mono bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded mx-1">
              JWT
            </span>
            . Para realizar uma requisição válida, é necessário incluir o
            cabeçalho:
            <div className="mt-4 bg-gray-50 border border-gray-100 rounded-md p-3 text-xs font-mono text-gray-800">
              Authorization: Bearer <em>{`<seu-token-jwt>`}</em>
            </div>
            <p className="mt-4">
              O token de autenticação pode ser obtido através da rota de
              integração específica da plataforma.
            </p>
          </div>
        </section>

        <div className="mb-10 mt-5">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            🧾 <span>Parâmetros da Consulta</span>
          </h3>

          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="min-w-full text-sm bg-white">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold border-b">
                    Nome
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b">
                    Tipo
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b">
                    Descrição
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b">
                    Obrigatório
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {(selectedQuery?.QueryCustomized?.parameterType === "CPF" ||
                  selectedQuery?.QueryCustomized?.parameterType ===
                    "CPF/CNPJ") && (
                  <tr className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 border-b">cpf</td>
                    <td className="px-5 py-3 border-b">string</td>
                    <td className="px-5 py-3 border-b">
                      Número do CPF (somente dígitos)
                    </td>
                    <td className="px-5 py-3 border-b">
                      {selectedQuery?.QueryCustomized?.parameterType === "CPF"
                        ? "Sim"
                        : "Opcional"}
                    </td>
                  </tr>
                )}

                {(selectedQuery?.QueryCustomized?.parameterType === "CNPJ" ||
                  selectedQuery?.QueryCustomized?.parameterType ===
                    "CPF/CNPJ") && (
                  <tr className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 border-b">cnpj</td>
                    <td className="px-5 py-3 border-b">string</td>
                    <td className="px-5 py-3 border-b">
                      Número do CNPJ (somente dígitos)
                    </td>
                    <td className="px-5 py-3 border-b">
                      {selectedQuery?.QueryCustomized?.parameterType === "CNPJ"
                        ? "Sim"
                        : "Opcional"}
                    </td>
                  </tr>
                )}

                {selectedQuery?.QueryCustomized?.parameterType === "PLACA" && (
                  <tr className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 border-b">placa</td>
                    <td className="px-5 py-3 border-b">string</td>
                    <td className="px-5 py-3 border-b">
                      Placa do veículo (ex: ABC1D23)
                    </td>
                    <td className="px-5 py-3 border-b">Sim</td>
                  </tr>
                )}

                {selectedQuery?.QueryCustomized?.parameterType === "CHASSI" && (
                  <tr className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 border-b">chassi</td>
                    <td className="px-5 py-3 border-b">string</td>
                    <td className="px-5 py-3 border-b">
                      Número do chassi do veículo
                    </td>
                    <td className="px-5 py-3 border-b">Sim</td>
                  </tr>
                )}

                {selectedQuery?.QueryCustomized?.parameterType === "MOTOR" && (
                  <tr className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 border-b">motor</td>
                    <td className="px-5 py-3 border-b">string</td>
                    <td className="px-5 py-3 border-b">
                      Número do motor do veículo
                    </td>
                    <td className="px-5 py-3 border-b">Sim</td>
                  </tr>
                )}

                {selectedQuery?.QueryCustomized?.parameterType ===
                  "PLACA/CHASSI" && (
                  <>
                    <tr className="hover:bg-gray-50 transition">
                      <td className="px-5 py-3 border-b">placa</td>
                      <td className="px-5 py-3 border-b">string</td>
                      <td className="px-5 py-3 border-b">
                        Placa do veículo (ex: ABC1D23)
                      </td>
                      <td className="px-5 py-3 border-b">Opcional</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition">
                      <td className="px-5 py-3 border-b">chassi</td>
                      <td className="px-5 py-3 border-b">string</td>
                      <td className="px-5 py-3 border-b">
                        Número do chassi do veículo
                      </td>
                      <td className="px-5 py-3 border-b">Opcional</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {selectedApi && selectedQuery && (
        <ApiRequestExamples
          selectedApi={selectedApi}
          selectedQuery={selectedQuery}
        />
      )}

      <section className="mb-12">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          📤 <span>Respostas da API</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div className="flex flex-col gap-2 border border-green-200 bg-green-50 text-green-800 p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 font-semibold">
              ✅ <span>200 - Sucesso</span>
            </div>
            <p className="text-green-900">
              Requisição executada com sucesso. A API retorna os dados
              solicitados.
            </p>
          </div>
          <div className="flex flex-col gap-2 border border-yellow-200 bg-yellow-50 text-yellow-800 p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 font-semibold">
              🔒 <span>401 / 403 - Sem permissão</span>
            </div>
            <p className="text-yellow-900">
              Token ausente/inválido ou sem acesso à consulta.
            </p>
          </div>
          <div className="flex flex-col gap-2 border border-red-200 bg-red-50 text-red-800 p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 font-semibold">
              ❌ <span>500 - Erro no provedor</span>
            </div>
            <p className="text-red-900">
              Falha na comunicação com o provedor. Tente novamente mais tarde.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-10 mt-5">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          ⚙️ <span>Testar API</span>
        </h3>

        <p className="text-sm text-gray-600 mb-2">
          Utilize essa função para <strong>obter uma resposta real</strong> da
          API com base no parâmetro informado.
        </p>

        <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-md px-3 py-2 text-sm leading-relaxed mb-3">
          ⚠️ Cada requisição realizada aqui será{" "}
          <strong>efetivamente cobrada</strong> como uma consulta normal.
        </div>

        {/* Você já tinha o bloco de teste abaixo — mantive igual */}
      </section>
    </>
  );

  return (
    <div className="flex text-gray-800">
      {/* Sidebar */}
      <aside className="w-80 min-w-80 bg-white border border-gray-300 shadow-sm px-6 py-8 hidden md:block top-0 overflow-y-auto">
        <h2 className="text-base font-bold text-green-700 mb-3 flex items-center gap-2 bg-white">
          📘 <span>Documentação de APIs</span>
        </h2>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="🔍 Buscar Consulta..."
          className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-400 mb-5"
        />

        <nav className="space-y-6">
          {groupedApis?.map((category) => (
            <div key={category.id}>
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1 px-1 hover:text-green-700 transition"
              >
                <div className="flex items-center gap-2">
                  {category.icon}
                  <span>{category.name}</span>
                </div>
                <IoIosArrowDown
                  className={`w-4 h-4 transform transition-transform duration-200 ${
                    expandedCategories[category.id] ? "rotate-180" : ""
                  }`}
                />
              </button>

              {expandedCategories[category.id] && (
                <ul className="space-y-1 ml-2 border-l-2 border-green-200 pl-3">
                  {category.apis.map((api) => (
                    <li key={api.id}>
                      <button
                        onClick={() => setSelectedApi(api)}
                        className={`w-full flex gap-1 text-left px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 uppercase truncate overflow-hidden whitespace-nowrap ${
                          selectedApi?.id === api.id
                            ? "bg-green-100 text-green-800 ring-1 ring-green-300"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        {api.status == 0 && (
                          <div className="flex gap-1 items-center">
                            <FaTools className="inline-block ml-1 text-red-500" />
                          </div>
                        )}
                        <span>{api.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 px-6 overflow-scroll">
        <div className="mx-auto bg-white shadow-sm border border-gray-100 rounded-md px-5 py-5 relative">
          {selectedApi ? (
            <>
              <header className="mb-5 border-b border-gray-100 pb-6">
                <h1 className="text-2xl font-bold flex items-center gap-3 text-gray-900">
                  {selectedApi.name}
                </h1>
                <p className="text-base text-gray-500 mt-2">
                  {selectedApi.description || "Sem descrição."}
                </p>
                {selectedApi.status == 0 && (
                  <div className="flex items-center gap-3 items-cente bg-yellow-50 border border-yellow-300 p-2 rounded-md mt-2">
                    <FaTools className="inline-block ml-1 text-red-500" />
                    <div>Consulta em manutenção no momento!</div>
                  </div>
                )}
              </header>

              {/* ✅ Se for o item custom (CV / Negativação), renderiza o componente novo.
    Caso contrário, renderiza a documentação padrão */}
              {selectedApi?.isCustom ? (
                <NegativeDocs token={token} />
              ) : (
                renderDefaultDocs()
              )}

              {/* O bloco de teste comum já está correto: só aparece quando NÃO é custom */}
              {!selectedApi?.isCustom && (
                <section className="mb-10 mt-5">
                  {/* ...seu bloco "⚙️ Testar API" exatamente como está... */}
                </section>
              )}

              {/* SE VOCÊ quiser manter o bloco de teste comum abaixo, deixe-o fora do renderDefaultDocs() */}
              {!selectedApi.isCustom && (
                <section className="mb-10 mt-5">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    ⚙️ <span>Testar API</span>
                  </h3>

                  <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-md px-3 py-2 text-sm leading-relaxed mb-3">
                    ⚠️ Cada requisição realizada aqui será{" "}
                    <strong>efetivamente cobrada</strong> como uma consulta
                    normal em produção.
                  </div>

                  <div className="border border-gray-200 bg-white p-4 rounded-xl shadow-sm text-sm text-gray-700 space-y-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-400 focus:outline-none"
                        placeholder="Digite o parâmetro"
                        value={testParam}
                        onChange={(e) =>
                          setTestParam(e.target.value.toUpperCase())
                        }
                      />
                      <button
                        onClick={handleTestApi}
                        disabled={
                          !testParam || isLoadingTest || selectedApi.isCustom
                        }
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-semibold disabled:opacity-60"
                        title={
                          selectedApi.isCustom
                            ? "Teste não disponível para esta seção"
                            : ""
                        }
                      >
                        {isLoadingTest ? (
                          <div className="flex items-center gap-2">
                            <Spinner size="sm" light />
                            Consultando...
                          </div>
                        ) : (
                          "Testar"
                        )}
                      </button>
                    </div>

                    {apiResponse && (
                      <div className="bg-gray-50 p-4 border border-gray-200 rounded-md text-xs font-mono overflow-auto max-h-[300px]">
                        <pre>{JSON.stringify(apiResponse, null, 2)}</pre>
                      </div>
                    )}

                    {testError && (
                      <div className="text-red-600 text-sm font-medium bg-red-50 p-3 rounded-md border border-red-200">
                        Erro:{" "}
                        {typeof testError === "string"
                          ? testError
                          : JSON.stringify(testError, null, 2)}
                      </div>
                    )}
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center text-gray-600 bg-gray-50 rounded-md border border-dashed border-gray-200">
              <div className="text-5xl mb-4">📂</div>
              <h2 className="text-lg font-semibold mb-2">
                Nenhuma API selecionada
              </h2>
              <p className="text-sm max-w-md">
                Use o menu lateral para navegar pelas categorias e selecionar
                uma API. A documentação completa será exibida aqui.
              </p>
            </div>
          )}
          {isLoadingSelectedQuery && <DrawerLoading />}
        </div>
      </main>
    </div>
  );
}
